import React, { Dispatch, SetStateAction } from "react";

import useForm from "commons/hooks/useForm";
import {
    calculateDefaultValue,
    createTableFilters,
    PartialFormType,
} from "commons/hooks/useTableFilterBase";

import { SetValueInterface, UpdateFormInterface } from "commons/types/form";
import { FilterFieldCollection, FilterType } from "commons/types/table";
import { ObjectPropertyType } from "commons/types/validation";

export type StaticTableFilterHook = {
    setValue: SetValueInterface;
    setForm: Dispatch<SetStateAction<PartialFormType>>;
    updateForm: UpdateFormInterface;
    clearStaticFilters: () => void;
    form: PartialFormType;
    filters: Array<FilterType>;
};

function useStaticTableFilter(
    fields: FilterFieldCollection,
    initialFormValues?: PartialFormType | null
): StaticTableFilterHook {
    const property: ObjectPropertyType = React.useMemo(
        () => ({
            type: "object",
            properties: {},
        }),
        []
    );

    const { setValue, setForm, updateForm, form } = useForm<PartialFormType>(
        property,
        initialFormValues ?? calculateDefaultValue(fields)
    );

    const filters: Array<FilterType> = React.useMemo(
        () => createTableFilters(fields, form),
        [fields, form]
    );

    const clearStaticFilters = React.useCallback(
        () => setForm(calculateDefaultValue(fields)),
        [setForm, fields]
    );

    return React.useMemo(
        () => ({
            setValue,
            setForm,
            updateForm,
            clearStaticFilters,
            form,
            filters,
        }),
        [setValue, setForm, updateForm, clearStaticFilters, form, filters]
    );
}

export default useStaticTableFilter;
